import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, Camera, CameraOff, Flashlight, FlashlightOff } from "lucide-react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType, NotFoundException } from "@zxing/library";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { isHarmlessScannerError } from "../../lib/scannerErrors";

const QR_PREFIX = "napoli:cust:";
const VIBRATE_OK = 60;
const RECENT_KEY = "napoli.scanner.recent";
const RECENT_MAX = 3;

const buzz = (ms) => {
  try {
    if (navigator?.vibrate) navigator.vibrate(ms);
  } catch (_) {
    /* ignore */
  }
};

const initialsOf = (first, last) => {
  const f = (first || "").trim();
  const l = (last || "").trim();
  return ((f.charAt(0) + l.charAt(0)) || "?").toUpperCase();
};

const loadRecent = () => {
  try {
    const raw = window.sessionStorage?.getItem(RECENT_KEY);
    return raw ? JSON.parse(raw).slice(0, RECENT_MAX) : [];
  } catch (_) {
    return [];
  }
};
const saveRecent = (list) => {
  try {
    window.sessionStorage?.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch (_) {
    /* ignore */
  }
};

/**
 * Staff scanner page: camera-driven QR detection + manual code fallback.
 *
 * Layout: vertical stack on espresso bg.
 *  - Eyebrow / headline
 *  - Camera viewport (square, gold L-corner brackets, scanning line)
 *  - Manual code input (single 6-char textbox)
 */
const ScannerPage = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const controlsRef = useRef(null);    // @zxing/browser stop controls
  const detectedRef = useRef(false);   // prevents double-trigger before unmount
  const lookingUpRef = useRef(false);
  const lastActivityRef = useRef(Date.now());

  const [permState, setPermState] = useState("loading"); // loading | granted | denied | nocam | inuse | unsupported
  const [flash, setFlash] = useState(null); // 'ok' | 'err' | null
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [recent, setRecent] = useState(() => loadRecent());
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  // Hybrid autostart + tap-to-start fallback. Initial render = false so we
  // try autostart first (Chrome + desktop). If autostart resolves but the
  // <video> stays paused after 3s (iOS Safari signature), flip to true and
  // surface the tap-to-scan overlay so the user gesture can unlock play().
  const [needsManualStart, setNeedsManualStart] = useState(false);

  // Track recent customers in session storage so a server can re-tap a
  // pill instead of re-scanning when they forgot to grab the check first
  // pass. Frontend-only state — no backend persistence.
  const pushRecent = (cust) => {
    const entry = {
      id: cust.id,
      first_name: cust.first_name || "",
      last_name: cust.last_name || "",
      tier: cust.current_tier || "ospite",
    };
    setRecent((prev) => {
      const dedup = prev.filter((p) => p.id !== entry.id);
      const next = [entry, ...dedup].slice(0, RECENT_MAX);
      saveRecent(next);
      return next;
    });
  };

  // Lookup helper used by both scan + manual
  const doLookup = async (payload) => {
    if (lookingUpRef.current) return;
    lookingUpRef.current = true;
    setSubmitting(true);
    try {
      const { data } = await api.post("/staff/lookup-customer", payload);
      const cust = data?.customer;
      if (cust?.id) {
        lastActivityRef.current = Date.now();
        pushRecent(cust);
        navigate(`/staff/customer/${cust.id}`);
      } else {
        toast.error("That didn't match a Napoli account.");
      }
    } catch (err) {
      const code = err?.response?.data?.detail?.error;
      if (code === "customer_not_found") {
        toast.error("Customer not found. Try again.");
      } else if (code === "invalid_qr") {
        toast.error("That doesn't look like a Napoli QR code.");
      } else {
        toast.error("Something went wrong. Try again.");
      }
      setFlash("err");
      setTimeout(() => setFlash(null), 600);
    } finally {
      setSubmitting(false);
      // Allow next scan after a short cooldown
      setTimeout(() => {
        lookingUpRef.current = false;
        detectedRef.current = false;
      }, 1200);
    }
  };

  const onScanSuccess = (decoded) => {
    if (detectedRef.current) return;
    if (typeof decoded !== "string" || !decoded.startsWith(QR_PREFIX)) {
      // Ignore non-Napoli QRs silently — staff may walk past unrelated codes.
      return;
    }
    detectedRef.current = true;
    lastActivityRef.current = Date.now();
    buzz(VIBRATE_OK);
    setFlash("ok");
    setTimeout(() => setFlash(null), 700);
    doLookup({ qr_payload: decoded });
  };

  // Global unhandled-rejection + error suppression for known-harmless
  // scanner-library errors. Kept after the @zxing/browser swap as defensive
  // depth — if the new library ever emits an async play() rejection (it
  // shouldn't, but iOS Safari is unpredictable), we still preventDefault for
  // patterns matched by isHarmlessScannerError. Real errors (permission
  // denied, NotReadableError, network failures, etc.) propagate normally.
  useEffect(() => {
    const onUnhandled = (event) => {
      if (isHarmlessScannerError(event.reason || event)) {
        event.preventDefault();
      }
    };
    const onError = (event) => {
      if (isHarmlessScannerError(event.error || event.message)) {
        event.preventDefault();
      }
    };
    window.addEventListener("unhandledrejection", onUnhandled);
    window.addEventListener("error", onError);
    return () => {
      window.removeEventListener("unhandledrejection", onUnhandled);
      window.removeEventListener("error", onError);
    };
  }, []);

  // ----- Pause camera while the tab is hidden, resume on return -----
  // @zxing/browser doesn't expose pause/resume directly, but we can stop the
  // video element's MediaStream tracks when hidden and let the browser pick
  // them back up when the page becomes visible. In practice, modern browsers
  // already pause <video> playback for background tabs, so this is belt-and-
  // suspenders; documented so future agents don't strip it.
  useEffect(() => {
    const onVis = () => {
      const v = videoRef.current;
      if (!v) return;
      if (document.hidden) {
        try { v.pause(); } catch (_) { /* ignore */ }
      } else {
        try {
          const p = v.play();
          if (p && typeof p.catch === "function") p.catch(() => {});
        } catch (_) { /* ignore */ }
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // ----- Scanner init (reusable: called on mount for autostart, and from
  // the manual-start button for iOS Safari, where getUserMedia + play() must
  // both fire inside a user gesture handler).
  const initScanner = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setPermState("unsupported");
      return false;
    }

    // Tear down any previous controls before re-init (defensive — manual
    // start may follow a half-started autostart attempt).
    if (controlsRef.current) {
      try { controlsRef.current.stop(); } catch (_) { /* ignore */ }
      controlsRef.current = null;
    }

    // Restrict to QR codes only — saves CPU on every frame compared to the
    // default "all barcode formats" pass.
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE]);
    const codeReader = new BrowserMultiFormatReader(hints);

    try {
      const constraints = {
        audio: false,
        video: { facingMode: { ideal: "environment" } },
      };
      const controls = await codeReader.decodeFromConstraints(
        constraints,
        videoRef.current,
        (result, err) => {
          if (result) {
            onScanSuccess(result.getText());
            return;
          }
          // NotFoundException = "no QR detected this frame" → completely
          // normal, ignore silently. Other errors are real and should flow
          // through the harmless-filter to the global handlers / boundary.
          if (err && !(err instanceof NotFoundException)) {
            if (isHarmlessScannerError(err)) return;
            // eslint-disable-next-line no-console
            console.warn("Scanner decode error:", err);
          }
        },
      );

      // Explicit play() — Chrome iOS handles autoplay fine for getUserMedia
      // streams; Safari iOS does NOT without an explicit call (and even then
      // not without a user gesture, which is what the manual-start flow gives
      // us). We still try here on autostart in case it works in Chrome iOS.
      try {
        const v = videoRef.current;
        if (v && v.paused) {
          const playPromise = v.play();
          if (playPromise && typeof playPromise.catch === "function") {
            await playPromise.catch((err) => {
              if (isHarmlessScannerError(err)) return;
              // eslint-disable-next-line no-console
              console.warn("Video play() failed:", err);
            });
          }
        }
      } catch (err) {
        if (!isHarmlessScannerError(err)) {
          // eslint-disable-next-line no-console
          console.warn("Video play() attempt threw:", err);
        }
      }

      controlsRef.current = controls;
      setPermState("granted");
      setNeedsManualStart(false);
      lastActivityRef.current = Date.now();

      // Detect torch support — read the live MediaStream from the <video>.
      try {
        const track = videoRef.current?.srcObject?.getVideoTracks?.()?.[0];
        const caps = track?.getCapabilities?.();
        if (caps && "torch" in caps) {
          setTorchSupported(true);
        }
      } catch (_) { /* best-effort */ }

      return true;
    } catch (e) {
      if (isHarmlessScannerError(e)) return false;
      const name = e?.name || "";
      const msg = String(e?.message || e || "");
      if (name === "NotAllowedError" || /permission/i.test(msg)) setPermState("denied");
      else if (name === "NotFoundError" || /no.+camera|no.+device/i.test(msg)) setPermState("nocam");
      else if (name === "NotReadableError" || /already in use/i.test(msg)) setPermState("inuse");
      else setPermState("denied");
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- Autostart on mount, with a 3-second fallback to tap-to-start.
  // Strategy:
  //   1. Call initScanner() immediately. This works for Chrome iOS + desktop.
  //   2. If it returns true but <video> is still paused 3s later, that's the
  //      iOS Safari signature — stream is attached but the element won't
  //      play without a user gesture. Surface the "Tap to scan" overlay.
  //   3. If it returns false, permState already reflects the terminal error
  //      (denied/nocam/inuse/unsupported); don't show the manual button.
  useEffect(() => {
    let cancelled = false;
    let timeoutId = null;

    (async () => {
      const success = await initScanner();
      if (cancelled) return;

      if (success) {
        timeoutId = setTimeout(() => {
          if (cancelled) return;
          const v = videoRef.current;
          if (v && v.paused) {
            // Stop the half-started scanner so the dark camera is released
            if (controlsRef.current) {
              try { controlsRef.current.stop(); } catch (_) { /* ignore */ }
              controlsRef.current = null;
            }
            try {
              const stream = v.srcObject;
              if (stream && typeof stream.getTracks === "function") {
                stream.getTracks().forEach((t) => { try { t.stop(); } catch (_) {} });
              }
              v.srcObject = null;
            } catch (_) { /* ignore */ }
            setNeedsManualStart(true);
            setPermState("loading"); // reset so the manual overlay can claim the layer
          }
        }, 3000);
      }
    })();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      const c = controlsRef.current;
      controlsRef.current = null;
      if (c) {
        try { c.stop(); } catch (_) { /* ignore */ }
      }
      // Explicit track stop for iOS Safari race safety
      try {
        const v = videoRef.current;
        if (v) {
          v.pause();
          const stream = v.srcObject;
          if (stream && typeof stream.getTracks === "function") {
            stream.getTracks().forEach((t) => { try { t.stop(); } catch (_) {} });
          }
          v.srcObject = null;
        }
      } catch (_) { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initScanner]);

  const handleManualStart = async () => {
    setNeedsManualStart(false);
    setPermState("loading");
    await initScanner();
  };

  // ----- Torch toggle -----
  const toggleTorch = async () => {
    try {
      const track = videoRef.current?.srcObject?.getVideoTracks?.()?.[0];
      if (!track) return;
      const next = !torchOn;
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch (_) {
      toast.error("Torch isn't available on this device.");
      setTorchSupported(false);
    }
  };

  const tapRecent = (entry) => {
    if (lookingUpRef.current) return;
    doLookup({ qr_payload: `${QR_PREFIX}${entry.id}` });
  };

  const onCodeChange = (e) => {
    const v = (e.target.value || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
    setCode(v);
  };

  const submitCode = (e) => {
    e?.preventDefault();
    if (!code || code.length < 4 || submitting) return;
    doLookup({ customer_code: code });
  };

  const cornerBase =
    "absolute w-10 h-10 border-napoli-gold transition-colors duration-200";
  const cornerColor = flash === "ok" ? "!border-green-400" : flash === "err" ? "!border-napoli-oxblood" : "";

  return (
    <section data-testid="scanner-page" className="px-6 pt-8 pb-12">
      <div className="max-w-xl mx-auto text-center">
        <p className="font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-gold">
          Ready to scan
        </p>
        <h1 className="mt-3 font-display font-bold text-[24px] sm:text-[28px] text-napoli-cream">
          Scan a customer&rsquo;s QR.
        </h1>
        <p className="mt-2 font-body italic text-[13px] text-napoli-cream/70">
          Or enter their code manually below.
        </p>
      </div>

      {/* Camera viewport */}
      <div className="mx-auto mt-8 relative" style={{ width: "min(80vw, 420px)" }}>
        <div
          data-testid="scanner-viewport"
          className="relative aspect-square bg-napoli-espresso-soft border-2 border-napoli-gold overflow-hidden"
        >
          <video
            ref={videoRef}
            data-testid="scanner-video"
            autoPlay
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Status fill if camera not running */}
          {needsManualStart ? (
            <div
              data-testid="scanner-manual-start"
              className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 bg-napoli-espresso-soft z-10"
            >
              <Camera size={32} className="text-napoli-gold" aria-hidden />
              <p className="mt-4 font-display font-bold text-[18px] text-napoli-cream">
                Ready when you are.
              </p>
              <p className="mt-1 font-body italic text-[12px] text-napoli-cream/70 max-w-[260px]">
                Tap below to start the camera.
              </p>
              <button
                type="button"
                data-testid="scanner-manual-start-button"
                onClick={handleManualStart}
                className="mt-5 bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[11px] tracking-[0.22em] uppercase px-6 py-3 hover:bg-napoli-oxblood-dark transition-colors inline-flex items-center gap-2"
              >
                <Camera size={14} aria-hidden />
                Tap to scan
              </button>
            </div>
          ) : null}

          {permState !== "granted" && !needsManualStart ? (
            <div
              data-testid={`scanner-status-${permState}`}
              className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 bg-napoli-espresso-soft"
            >
              {permState === "loading" ? (
                <>
                  <Loader2 size={28} className="animate-spin text-napoli-gold" />
                  <p className="mt-3 font-body text-[12px] text-napoli-cream/70">
                    Starting camera…
                  </p>
                </>
              ) : permState === "denied" ? (
                <>
                  <CameraOff size={28} className="text-napoli-oxblood" />
                  <p className="mt-3 font-body font-semibold text-[14px] text-napoli-cream">
                    Camera access denied.
                  </p>
                  <p className="mt-1 font-body italic text-[12px] text-napoli-cream/70">
                    Use the code below ↓
                  </p>
                </>
              ) : permState === "nocam" ? (
                <>
                  <Camera size={28} className="text-napoli-cream/60" />
                  <p className="mt-3 font-body font-semibold text-[14px] text-napoli-cream">
                    No camera found.
                  </p>
                  <p className="mt-1 font-body italic text-[12px] text-napoli-cream/70">
                    Use manual entry below.
                  </p>
                </>
              ) : permState === "inuse" ? (
                <>
                  <AlertCircle size={28} className="text-napoli-gold" />
                  <p className="mt-3 font-body font-semibold text-[14px] text-napoli-cream">
                    Camera is busy.
                  </p>
                  <p className="mt-1 font-body italic text-[12px] text-napoli-cream/70">
                    Close other apps using the camera, or use the code below.
                  </p>
                </>
              ) : (
                <>
                  <Camera size={28} className="text-napoli-cream/60" />
                  <p className="mt-3 font-body font-semibold text-[14px] text-napoli-cream">
                    Camera not supported.
                  </p>
                  <p className="mt-1 font-body italic text-[12px] text-napoli-cream/70">
                    Use manual entry below.
                  </p>
                </>
              )}
            </div>
          ) : null}

          {/* L-shaped corner brackets */}
          <div
            aria-hidden
            className={`${cornerBase} ${cornerColor} top-2 left-2 border-t-2 border-l-2 ${flash !== "err" ? "animate-pulse" : ""}`}
          />
          <div
            aria-hidden
            className={`${cornerBase} ${cornerColor} top-2 right-2 border-t-2 border-r-2 ${flash !== "err" ? "animate-pulse" : ""}`}
          />
          <div
            aria-hidden
            className={`${cornerBase} ${cornerColor} bottom-2 left-2 border-b-2 border-l-2 ${flash !== "err" ? "animate-pulse" : ""}`}
          />
          <div
            aria-hidden
            className={`${cornerBase} ${cornerColor} bottom-2 right-2 border-b-2 border-r-2 ${flash !== "err" ? "animate-pulse" : ""}`}
          />

          {/* Scanning laser */}
          {permState === "granted" ? (
            <div aria-hidden className="absolute inset-x-6 top-1/2 h-px bg-napoli-gold/80 scanline" />
          ) : null}

          {/* Torch toggle (visible only when supported) */}
          {torchSupported && permState === "granted" ? (
            <button
              type="button"
              data-testid="scanner-torch-toggle"
              onClick={toggleTorch}
              aria-pressed={torchOn}
              aria-label={torchOn ? "Turn torch off" : "Turn torch on"}
              className={[
                "absolute top-3 right-3 w-10 h-10 flex items-center justify-center",
                "border transition-colors",
                torchOn
                  ? "bg-napoli-gold text-napoli-espresso border-napoli-gold"
                  : "bg-napoli-espresso/70 text-napoli-cream border-napoli-gold/50 hover:border-napoli-gold",
              ].join(" ")}
            >
              {torchOn ? <Flashlight size={18} /> : <FlashlightOff size={18} />}
            </button>
          ) : null}
        </div>

        <p className="mt-3 font-body italic text-[12px] text-napoli-cream/60 text-center">
          Position the QR code in the frame.
        </p>

        {/* Recent-3 pills — session-only memory of who Marco has scanned tonight */}
        {recent.length > 0 ? (
          <div
            data-testid="scanner-recent-pills"
            className="mt-5 flex flex-wrap justify-center gap-2"
            aria-label="Recent customers"
          >
            <span className="font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-cream/55 self-center mr-1">
              Last 3
            </span>
            {recent.map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`scanner-recent-${p.id}`}
                onClick={() => tapRecent(p)}
                className="inline-flex items-center gap-2 bg-napoli-espresso-soft border border-napoli-gold/30 hover:border-napoli-gold px-3 py-1.5 transition-colors group"
              >
                <span
                  aria-hidden
                  className="h-6 w-6 rounded-full bg-napoli-cream-warm border border-napoli-gold text-napoli-oxblood flex items-center justify-center font-display font-bold text-[10px]"
                >
                  {initialsOf(p.first_name, p.last_name)}
                </span>
                <span className="font-body text-[12px] text-napoli-cream group-hover:text-napoli-gold-light">
                  {p.first_name}
                </span>
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {/* OR divider */}
      <div className="max-w-md mx-auto mt-10 flex items-center gap-4">
        <span className="flex-1 h-px bg-napoli-gold/30" />
        <span className="font-body text-[10px] tracking-[0.3em] uppercase text-napoli-cream/60">
          Or
        </span>
        <span className="flex-1 h-px bg-napoli-gold/30" />
      </div>

      {/* Manual entry */}
      <form
        onSubmit={submitCode}
        className="max-w-md mx-auto mt-6 px-2"
        data-testid="scanner-manual-form"
      >
        <label className="block">
          <span className="block font-body font-semibold text-[11px] tracking-[0.22em] uppercase text-napoli-cream/70">
            Enter customer code
          </span>
          <input
            data-testid="scanner-code-input"
            value={code}
            onChange={onCodeChange}
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            placeholder="ABC123"
            maxLength={6}
            className="mt-2 w-full bg-napoli-cream-warm border border-napoli-gold/40 text-napoli-espresso px-4 py-3 font-mono text-[18px] tracking-[0.2em] focus:outline-none focus:border-napoli-oxblood transition-colors uppercase"
          />
        </label>

        <button
          type="submit"
          data-testid="scanner-code-submit"
          disabled={!code || code.length < 4 || submitting}
          className="mt-4 w-full bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-4 hover:bg-napoli-oxblood-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Looking up…
            </>
          ) : (
            "Look up customer"
          )}
        </button>
      </form>

      <style>{`
        @keyframes scanline-anim {
          0% { transform: translateY(-90%); opacity: 0.4; }
          50% { opacity: 1; }
          100% { transform: translateY(90%); opacity: 0.4; }
        }
        .scanline {
          animation: scanline-anim 2.4s ease-in-out infinite;
          box-shadow: 0 0 12px rgba(197,160,77,0.55);
        }
      `}</style>
    </section>
  );
};

export default ScannerPage;
